import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { UserPlus, User, Mail, Lock, AlertCircle, Loader2, CheckCircle } from 'lucide-react'
import { AuthService } from '../services/authService'
import { loginSuccess, loginFailure, setLoading } from '../store/slices/authSlice'
import type { RootState } from '../store'
import { useBranding } from '../hooks/useBranding'
import { DEFAULT_APP_HOME } from '../config/routes'
import { withTimezone } from '../utils/signupTimezone'

// Google Icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
)

export const Register: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { isAuthenticated } = useSelector((state: RootState) => state.auth)
  const branding = useBranding()
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: ''
  })
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const authService = new AuthService()

  // Google-only sign-up (production parity): the password fields are hidden and
  // "Sign up with Google" is the sole path. Defaults ON; set
  // VITE_AUTH_GOOGLE_ONLY=false to expose the password form. New accounts are
  // created pending and require admin approval before they can sign in.
  const googleOnly =
    (((import.meta as any).env?.VITE_AUTH_GOOGLE_ONLY ?? 'true') as string) !== 'false'

  // Check for OAuth error in URL params
  useEffect(() => {
    const oauthError = searchParams.get('error')
    if (oauthError) {
      setError(`Google sign-up failed: ${oauthError.replace(/_/g, ' ')}`)
    }
  }, [searchParams])

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(DEFAULT_APP_HOME, { replace: true })
    }
  }, [isAuthenticated, navigate])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.username.trim()) {
      errors.username = 'Username is required'
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters'
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }

    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    }

    if (!formData.confirm_password) {
      errors.confirm_password = 'Please confirm your password'
    } else if (formData.password !== formData.confirm_password) {
      errors.confirm_password = 'Passwords do not match'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setValidationErrors({})

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    dispatch(setLoading(true))

    try {
      const response = await authService.register(formData)
      
      // If admin approval is required, don't log in. Send to login with message.
      if (response.success && response.approval_required) {
        navigate('/login', { 
          replace: true,
          state: { message: 'Account creation pending admin approval. Please await email for more instructions.' }
        })
        return
      }

      if (response.success && response.user) {
        dispatch(loginSuccess({ 
          user: {
            id: response.user.id.toString(),
            username: response.user.username,
            email: response.user.email
          },
          token: 'session'
        }))
        
        // Redirect to Today's Focus after successful registration
        navigate(DEFAULT_APP_HOME, { replace: true })
      } else {
        setError(response.error || 'Registration failed')
        dispatch(loginFailure())
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.'
      setError(errorMessage)
      dispatch(loginFailure())
    } finally {
      setIsSubmitting(false)
      dispatch(setLoading(false))
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-default py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={branding.icon} alt={branding.name} className="mx-auto h-16 w-16 mb-4" />
          <h2 className="text-3xl font-extrabold text-primary">
            Let's get started
          </h2>
          <p className="mt-2 text-sm text-secondary">
            Can't wait to work with you. Let's make some real progress! 😊
          </p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>Register</CardTitle>
            <CardDescription>
              {googleOnly
                ? 'Sign up with your Google account. New accounts require admin approval before you can sign in.'
                : 'Fill in your details to create a new account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 2026-08-29: the error appeared silently — no live region — and
                  no field error was tied to the input it belongs to. */}
              <div aria-live="assertive" aria-atomic="true">
                {error && (
                  <div
                    id="register-error"
                    role="alert"
                    className="p-3 bg-tint-bad-veil border border-tint-bad-edge rounded-lg flex items-center space-x-2"
                  >
                    <AlertCircle className="h-5 w-5 text-bad flex-shrink-0" aria-hidden="true" />
                    <p className="text-sm text-bad">{error}</p>
                  </div>
                )}
              </div>

              {!googleOnly && (
              <>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-secondary mb-1">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-muted" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    autoComplete="username"
                    aria-invalid={validationErrors.username ? true : undefined}
                    aria-describedby={validationErrors.username ? 'username-error' : undefined}
                    value={formData.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-surface text-primary ${
                      validationErrors.username 
                        ? 'border-bad' 
                        : 'border-soft'
                    }`}
                    placeholder="Choose a username"
                    disabled={isSubmitting}
                  />
                </div>
                {validationErrors.username && (
                  <p id="username-error" className="mt-1 text-sm text-bad">{validationErrors.username}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-muted" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    aria-invalid={validationErrors.email ? true : undefined}
                    aria-describedby={validationErrors.email ? 'email-error' : undefined}
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-surface text-primary ${
                      validationErrors.email 
                        ? 'border-bad' 
                        : 'border-soft'
                    }`}
                    placeholder="Enter your email"
                    disabled={isSubmitting}
                  />
                </div>
                {validationErrors.email && (
                  <p id="email-error" className="mt-1 text-sm text-bad">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-secondary mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-muted" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="new-password"
                    aria-invalid={validationErrors.password ? true : undefined}
                    aria-describedby={validationErrors.password ? 'password-error password-hint' : 'password-hint'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-surface text-primary ${
                      validationErrors.password 
                        ? 'border-bad' 
                        : 'border-soft'
                    }`}
                    placeholder="Create a password"
                    disabled={isSubmitting}
                  />
                </div>
                {validationErrors.password && (
                  <p id="password-error" className="mt-1 text-sm text-bad">{validationErrors.password}</p>
                )}
                <p id="password-hint" className="mt-1 text-xs text-secondary">
                  Must be at least 6 characters
                </p>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-secondary mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-muted" />
                  </div>
                  <input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    required
                    autoComplete="new-password"
                    aria-invalid={validationErrors.confirm_password ? true : undefined}
                    aria-describedby={validationErrors.confirm_password ? 'confirm-password-error' : undefined}
                    value={formData.confirm_password}
                    onChange={(e) => handleChange('confirm_password', e.target.value)}
                    className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-surface text-primary ${
                      validationErrors.confirm_password 
                        ? 'border-bad' 
                        : formData.password && formData.password === formData.confirm_password
                        ? 'border-ok'
                        : 'border-soft'
                    }`}
                    placeholder="Confirm your password"
                    disabled={isSubmitting}
                  />
                  {formData.password && formData.password === formData.confirm_password && !validationErrors.confirm_password && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <CheckCircle className="h-5 w-5 text-ok" />
                    </div>
                  )}
                </div>
                {validationErrors.confirm_password && (
                  <p id="confirm-password-error" className="mt-1 text-sm text-bad">{validationErrors.confirm_password}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-brand hover:bg-brand-hover text-on-brand"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create account
                  </>
                )}
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-soft"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-surface text-secondary">or continue with</span>
                </div>
              </div>
              </>
              )}

              {/* Google Sign Up Button */}
              <Button
                type="button"
                variant={googleOnly ? 'default' : 'outline'}
                className={googleOnly
                  ? 'w-full bg-surface border border-soft hover:bg-surface-alt text-primary'
                  : 'w-full border-soft hover:bg-surface-alt'}
                disabled={isGoogleLoading}
                onClick={() => {
                  setIsGoogleLoading(true)
                  window.location.href = withTimezone('/auth/google')
                }}
              >
                {isGoogleLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <GoogleIcon />
                    <span className="ml-2">Sign up with Google</span>
                  </>
                )}
              </Button>

              <div className="text-center text-sm mt-4">
                <span className="text-secondary">Already have an account? </span>
                <Link to="/login" className="font-medium text-brand hover:text-brand-hover">
                  Sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
